import { z } from "zod";
import { zaokrouhlitKc } from "../lib/money";

/**
 * HCA Moravia — ekonomika per seminář (Sprint 47 / §DH-02).
 * Výnosy = SUM hca_faktury WHERE termin.id = X AND stav = ZAPLACENA.
 * Licence (model majitele 2026-08) = max(výnosy − licenceOdpocetKcOsDen ×
 * počet aktivních přihlášek × počet dní, 0) × licencePct / 100.
 * Náklady = fixní × pocetDni + lektor + asistent + prostor + občerstvení
 * + per-účastník + admin paušál + ostatní + položky navíc + licence.
 */

const kcField = z.coerce
  .number()
  .nonnegative("Částka nesmí být záporná")
  .max(100_000_000, "Částka je mimo povolený rozsah");

/** Upsert per-termín náklady (admin). */
export const upsertNakladySchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
  fixniNakladKcPerDen: kcField.optional(),
  lektorFeeKc: kcField.optional(),
  asistentFeeKc: kcField.optional(),
  prostorKc: kcField.optional(),
  obcerstveniKc: kcField.optional(),
  /** Náklad na jednoho účastníka (skripta, občerstvení/hlava…) × obsazenost. */
  nakladPerUcastnikKc: kcField.optional(),
  /** Paušální administrativní poplatek za termín (např. 500 Kč za agendu). */
  adminPoplatekKc: kcField.optional(),
  ostatniKc: kcField.optional(),
  ostatniPopis: z.string().max(2000, "Popis musí být 2000 znaků nebo méně").optional(),
});

export const getEkonomikaSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
});

/**
 * Libovolné nákladové položky navíc u termínu (doprava, tisk…) —
 * tabulka `hca_terminy_naklady_polozky`, ekonomika je přičítá jako
 * samostatnou řádku breakdownu „Ostatní položky".
 */
export const addNakladPolozkaSchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
  nazev: z
    .string()
    .min(1, "Název položky je povinný")
    .max(255, "Název musí být 255 znaků nebo méně"),
  castkaKc: kcField,
});

export const removeNakladPolozkaSchema = z.object({
  id: z.string().uuid("Neplatné ID položky"),
});

export const listNakladPolozkySchema = z.object({
  terminId: z.string().uuid("Neplatné ID termínu"),
});

export type AddNakladPolozkaInput = z.infer<typeof addNakladPolozkaSchema>;
export type RemoveNakladPolozkaInput = z.infer<typeof removeNakladPolozkaSchema>;
export type ListNakladPolozkyInput = z.infer<typeof listNakladPolozkySchema>;

/** Top N profitable / loss-making. */
export const listTopEkonomikaSchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
  period: z.enum(["month", "quarter", "year", "all"]).default("month"),
});

export type UpsertNakladyInput = z.infer<typeof upsertNakladySchema>;
export type GetEkonomikaInput = z.infer<typeof getEkonomikaSchema>;
export type ListTopEkonomikaInput = z.infer<typeof listTopEkonomikaSchema>;

/**
 * Core ekonomika výpočet — pure function nad raw payloadem.
 * Vrací breakdown + zisk + marže.
 */
export interface EkonomikaInput {
  vynosyKc: number;
  pocetDni: number;
  licencePct: number;
  /**
   * Licence — odpočet Kč na osobu a den (model majitele, 2026-08).
   * Základ licence = výnosy − odpočet × pocetUcastniku × pocetDni, min 0.
   * 0 = licence z plných výnosů (staré chování).
   */
  licenceOdpocetKcOsDen: number;
  fixniNakladKcPerDen: number;
  lektorFeeKc: number;
  asistentFeeKc: number;
  prostorKc: number;
  obcerstveniKc: number;
  /** Náklad na účastníka — násobí se `pocetUcastniku` (obsazenost termínu). */
  nakladPerUcastnikKc: number;
  /**
   * Počet účastníků termínu (kapacitaObsazena = denormalizovaný počet
   * aktivních přihlášek; blokovaná místa bez osoby se nepočítají).
   */
  pocetUcastniku: number;
  /** Paušální admin poplatek za termín. */
  adminPoplatekKc: number;
  ostatniKc: number;
  /** Součet libovolných nákladových položek navíc (hca_terminy_naklady_polozky). */
  polozkyKc: number;
}

export interface EkonomikaResult {
  vynosyKc: number;
  nakladyKc: {
    fixni: number;
    lektor: number;
    asistent: number;
    prostor: number;
    obcerstveni: number;
    perUcastnik: number;
    adminPoplatek: number;
    licence: number;
    ostatni: number;
    /** Součet libovolných položek navíc („Ostatní položky"). */
    polozky: number;
    total: number;
  };
  ziskKc: number;
  marzePct: number;
}

export function computeEkonomika(input: EkonomikaInput): EkonomikaResult {
  // Guard proti 0/záporným dnům; půldenní workshop platí polovinu fixu.
  const fixni = input.fixniNakladKcPerDen * Math.max(input.pocetDni, 0.5);
  const perUcastnik = input.nakladPerUcastnikKc * Math.max(input.pocetUcastniku, 0);
  // Licence — vzorec majitele (2026-08):
  //   základ = výnosy − licenceOdpocetKcOsDen × počet aktivních přihlášek × počet dní
  //   licence = max(základ, 0) × licencePct / 100
  // Půlden (0,5) odečítá polovinu odpočtu; záporný základ se ořízne na 0
  // (licence nikdy není záporná = nikdy nezvyšuje zisk).
  const licenceZaklad = Math.max(
    input.vynosyKc -
      input.licenceOdpocetKcOsDen * Math.max(input.pocetUcastniku, 0) * Math.max(input.pocetDni, 0),
    0,
  );
  const licence = licenceZaklad * (input.licencePct / 100);
  const total =
    fixni +
    input.lektorFeeKc +
    input.asistentFeeKc +
    input.prostorKc +
    input.obcerstveniKc +
    perUcastnik +
    input.adminPoplatekKc +
    licence +
    input.ostatniKc +
    input.polozkyKc;
  const zisk = input.vynosyKc - total;
  const marzePct = input.vynosyKc > 0 ? (zisk / input.vynosyKc) * 100 : 0;
  // Peněžní hodnoty jednotně přes zaokrouhlitKc (HALF_UP na haléře, robustní
  // vůči floatu) — marže není Kč, zůstává na 1 desetinném místě.
  return {
    vynosyKc: zaokrouhlitKc(input.vynosyKc),
    nakladyKc: {
      fixni: zaokrouhlitKc(fixni),
      lektor: zaokrouhlitKc(input.lektorFeeKc),
      asistent: zaokrouhlitKc(input.asistentFeeKc),
      prostor: zaokrouhlitKc(input.prostorKc),
      obcerstveni: zaokrouhlitKc(input.obcerstveniKc),
      perUcastnik: zaokrouhlitKc(perUcastnik),
      adminPoplatek: zaokrouhlitKc(input.adminPoplatekKc),
      licence: zaokrouhlitKc(licence),
      ostatni: zaokrouhlitKc(input.ostatniKc),
      polozky: zaokrouhlitKc(input.polozkyKc),
      total: zaokrouhlitKc(total),
    },
    ziskKc: zaokrouhlitKc(zisk),
    marzePct: Math.round(marzePct * 10) / 10,
  };
}
